import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Upload, File, Trash2, Download, X, ChevronLeft, ChevronRight, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import FileViewerModal from './FileViewerModal';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const MAX_IMAGE_DIMENSION = 3000;
const IMAGE_QUALITY = 0.9;
const MAX_FILE_SIZE = 50 * 1024 * 1024;

async function processImage(file: File): Promise<File> {
  const fileExt = file.name.split('.').pop()?.toLowerCase();
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
  const isImage = file.type.startsWith('image/') || (fileExt && imageExtensions.includes(fileExt));

  if (!isImage) {
    console.log(`File ${file.name} is not an image, skipping processing`);
    return file;
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File size exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB limit`);
  }

  console.log(`Processing image: ${file.name}, size: ${file.size} bytes`);

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error('Image processing timeout - file may be corrupted'));
    }, 30000);

    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      if (!e.target?.result) {
        clearTimeout(timeout);
        reject(new Error('Failed to read file data'));
        return;
      }
      img.src = e.target.result as string;
    };

    reader.onerror = () => {
      clearTimeout(timeout);
      reject(new Error('Failed to read image file'));
    };

    img.onload = () => {
      try {
        const { width, height } = img;
        const maxDimension = Math.max(width, height);

        console.log(`Image dimensions: ${width}x${height}`);

        if (maxDimension <= MAX_IMAGE_DIMENSION) {
          clearTimeout(timeout);
          console.log('Image within size limit, no processing needed');
          resolve(file);
          return;
        }

        const scale = MAX_IMAGE_DIMENSION / maxDimension;
        const newWidth = Math.round(width * scale);
        const newHeight = Math.round(height * scale);

        console.log(`Downscaling to: ${newWidth}x${newHeight}`);

        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          clearTimeout(timeout);
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        const createFileFromBlob = (blob: Blob): File => {
          const fileName = file.name.replace(/\.[^/.]+$/, '.jpg');
          try {
            return new window.File([blob], fileName, { type: 'image/jpeg' });
          } catch (e) {
            const fileBlob = blob as any;
            fileBlob.name = fileName;
            fileBlob.lastModified = Date.now();
            return fileBlob as File;
          }
        };

        if (typeof canvas.toBlob === 'function') {
          canvas.toBlob(
            (blob) => {
              clearTimeout(timeout);
              if (!blob) {
                reject(new Error('Failed to create blob from canvas'));
                return;
              }

              try {
                const processedFile = createFileFromBlob(blob);
                console.log(`Image processed successfully, new size: ${processedFile.size} bytes`);
                resolve(processedFile);
              } catch (err) {
                reject(err);
              }
            },
            'image/jpeg',
            IMAGE_QUALITY
          );
        } else {
          clearTimeout(timeout);
          const dataUrl = canvas.toDataURL('image/jpeg', IMAGE_QUALITY);
          fetch(dataUrl)
            .then(res => res.blob())
            .then(blob => {
              const processedFile = createFileFromBlob(blob);
              console.log(`Image processed successfully, new size: ${processedFile.size} bytes`);
              resolve(processedFile);
            })
            .catch(err => reject(err));
        }
      } catch (err) {
        clearTimeout(timeout);
        reject(err);
      }
    };

    img.onerror = () => {
      clearTimeout(timeout);
      reject(new Error('Failed to load image - file may be corrupted'));
    };

    reader.readAsDataURL(file);
  });
}

interface SortableImageProps {
  file: any;
  index: number;
  canEdit: boolean;
  onDelete: (id: string, path: string) => void;
  onClick: (index: number) => void;
}

function SortableImage({ file, index, canEdit, onDelete, onClick }: SortableImageProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: file.id, disabled: !canEdit });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative aspect-square bg-zinc-800 rounded-lg overflow-hidden"
    >
      <img
        src={file.file_path}
        alt={file.filename}
        className="w-full h-full object-cover cursor-pointer transition-transform group-hover:scale-105"
        onClick={() => onClick(index)}
      />
      {canEdit && (
        <>
          <button
            {...attributes}
            {...listeners}
            className="absolute top-2 left-2 w-8 h-8 bg-zinc-900/80 hover:bg-zinc-900 text-white rounded-full flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing"
            title="Drag to reorder"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(file.id, file.file_path);
            }}
            className="absolute top-2 right-2 w-8 h-8 bg-red-600/80 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
            title="Delete image"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  );
}

interface FileManagerProps {
  eventId: string;
  section: string;
  title: string;
  acceptedFileTypes?: string;
  showImageCarousel?: boolean;
  canEdit?: boolean;
}

export default function FileManager({
  eventId,
  section,
  title,
  acceptedFileTypes = '*',
  showImageCarousel = false,
  canEdit = true
}: FileManagerProps) {
  const [files, setFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState<any | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadFiles();
  }, [eventId, section]);

  const loadFiles = async () => {
    try {
      const { data, error } = await supabase
        .from('files')
        .select('*')
        .eq('event_id', eventId)
        .eq('section', section)
        .eq('is_latest', true)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;
      setFiles(data || []);
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles = e.target.files;
    if (!uploadedFiles || uploadedFiles.length === 0) return;

    setUploading(true);
    try {
      const { data: maxSortData } = await supabase
        .from('files')
        .select('sort_order')
        .eq('event_id', eventId)
        .eq('section', section)
        .order('sort_order', { ascending: false })
        .limit(1);

      let nextSortOrder = (maxSortData?.[0]?.sort_order ?? -1) + 1;

      for (const file of Array.from(uploadedFiles)) {
        const processedFile = await processImage(file);

        const fileExt = processedFile.name.split('.').pop();
        const fileName = `${eventId}/${section}/${Date.now()}-${processedFile.name}`;

        const { error: uploadError } = await supabase.storage
          .from('production-files')
          .upload(fileName, processedFile);

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from('production-files')
          .getPublicUrl(fileName);

        await supabase.from('files').insert({
          event_id: eventId,
          section: section,
          filename: processedFile.name,
          file_path: urlData.publicUrl,
          file_size: processedFile.size,
          file_type: processedFile.type || fileExt,
          sort_order: nextSortOrder++,
        });
      }

      await loadFiles();
    } catch (error) {
      console.error('Error uploading files:', error);
      alert(error instanceof Error ? error.message : 'Failed to upload file');
    } finally {
      setUploading(false);
      if (e.target) {
        e.target.value = '';
      }
    }
  };

  const handleDelete = async (fileId: string, filePath: string) => {
    try {
      const pathParts = filePath.split('/production-files/');
      if (pathParts.length > 1) {
        await supabase.storage
          .from('production-files')
          .remove([pathParts[1]]);
      }

      await supabase.from('files').delete().eq('id', fileId);
      const updatedFiles = files.filter(f => f.id !== fileId);
      setFiles(updatedFiles);

      if (showImageCarousel) {
        if (currentImageIndex >= updatedFiles.length && updatedFiles.length > 0) {
          setCurrentImageIndex(updatedFiles.length - 1);
        } else if (updatedFiles.length === 0) {
          setCurrentImageIndex(0);
          setLightboxOpen(false);
        }
      }
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const handleDownload = (filePath: string, filename: string) => {
    const link = document.createElement('a');
    link.href = filePath;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const imageFiles = files.filter(f =>
      f.file_type?.startsWith('image/') ||
      /\.(jpg|jpeg|png|gif|webp)$/i.test(f.filename)
    );

    const oldIndex = imageFiles.findIndex(f => f.id === active.id);
    const newIndex = imageFiles.findIndex(f => f.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedImages = arrayMove(imageFiles, oldIndex, newIndex);

    const allFiles = files.filter(f => !imageFiles.some(img => img.id === f.id));
    const newFiles = [...reorderedImages, ...allFiles];
    setFiles(newFiles);

    try {
      const updates = reorderedImages.map((file, index) => ({
        id: file.id,
        sort_order: index,
      }));

      for (const update of updates) {
        await supabase
          .from('files')
          .update({ sort_order: update.sort_order })
          .eq('id', update.id);
      }
    } catch (error) {
      console.error('Error updating sort order:', error);
      await loadFiles();
    }
  };

  const imageFiles = files.filter(f =>
    f.file_type?.startsWith('image/') ||
    /\.(jpg|jpeg|png|gif|webp)$/i.test(f.filename)
  );

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % imageFiles.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + imageFiles.length) % imageFiles.length);
  };

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  if (loading) {
    return (
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
        <Skeleton className="h-6 w-48 mb-4 bg-zinc-800" />
        <Skeleton className="h-32 w-full bg-zinc-800" />
      </div>
    );
  }

  return (
    <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        {canEdit && (
          <div className="flex-shrink-0">
            <input
              type="file"
              id={`file-upload-${section}`}
              multiple
              accept={acceptedFileTypes}
              onChange={handleFileUpload}
              className="hidden"
            />
            <label htmlFor={`file-upload-${section}`}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={uploading}
                className="text-zinc-400 hover:text-white hover:bg-zinc-800"
                onClick={() => document.getElementById(`file-upload-${section}`)?.click()}
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </label>
          </div>
        )}
      </div>

      {showImageCarousel && imageFiles.length > 0 ? (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={imageFiles.map(f => f.id)}
              strategy={rectSortingStrategy}
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {imageFiles.map((file, index) => (
                  <SortableImage
                    key={file.id}
                    file={file}
                    index={index}
                    canEdit={canEdit}
                    onDelete={handleDelete}
                    onClick={openLightbox}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {lightboxOpen && (
            <div
              className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
              onClick={closeLightbox}
            >
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="absolute top-4 left-4 bg-black/70 text-white text-sm px-3 py-1 rounded">
                {currentImageIndex + 1} / {imageFiles.length}
              </div>

              <div
                className="relative max-w-7xl max-h-[90vh] flex items-center justify-center"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={imageFiles[currentImageIndex].file_path}
                  alt={imageFiles[currentImageIndex].filename}
                  className="max-w-full max-h-[90vh] w-auto h-auto object-contain"
                />
              </div>

              {imageFiles.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      prevImage();
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      nextImage();
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}
            </div>
          )}
        </>
      ) : files.length > 0 ? (
        <div className="space-y-2">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
              onClick={() => setViewerFile(file)}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <File className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{file.filename}</p>
                  <p className="text-xs text-zinc-500">
                    {file.file_size ? `${(file.file_size / 1024).toFixed(1)} KB` : 'Unknown size'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(file.file_path, file.filename);
                  }}
                  className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-zinc-700"
                >
                  <Download className="w-4 h-4" />
                </Button>
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(file.id, file.file_path);
                    }}
                    className="h-8 w-8 text-zinc-400 hover:text-red-400 hover:bg-zinc-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-zinc-500">
          <File className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No files uploaded yet</p>
        </div>
      )}

      {viewerFile && (
        <FileViewerModal
          file={viewerFile}
          onClose={() => setViewerFile(null)}
          onDelete={
            canEdit
              ? () => {
                  handleDelete(viewerFile.id, viewerFile.file_path);
                  setViewerFile(null);
                }
              : undefined
          }
          canEdit={canEdit}
        />
      )}
    </div>
  );
}
